import { useCallback, useMemo } from 'react'
import { useIntlayer } from 'react-intlayer'
import { useDataTable } from '@/hooks/use-data-table'
import { archiveWorkflow, deleteWorkflow } from '../application/manage-workflow'
import { workflows as workflowsPort, notify } from '../wiring'
import type { CompanyWorkflow } from '../domain/workflow'
import { useAllWorkflows } from '../hooks/use-all-workflows'
import { useWorkflowColumns } from './workflows-list-columns'
import { WorkflowCreateSheet } from './workflows-create-sheet'
import { WorkflowsListHeader } from './workflows-list-header'
import { WorkflowsTable } from './workflows-table'
import { useWorkflowListParams } from './use-workflow-list-params'
import { PageLayoutContainer } from '@/components/page-layout'

const WorkflowList = () => {
	useIntlayer('workflows-list-page')
	const { q, sortBy, sortDir } = useWorkflowListParams()
	const { workflows, reload } = useAllWorkflows()

	const filtered = useMemo(() => {
		let result = workflows

		if (q) {
			const lower = q.toLowerCase()
			result = result.filter(w => w.name.toLowerCase().includes(lower) || w.description?.toLowerCase().includes(lower))
		}

		if (sortBy === 'name') {
			result = [...result].sort((a, b) => {
				const cmp = a.name.localeCompare(b.name)
				return sortDir === 'desc' ? -cmp : cmp
			})
		} else if (sortBy === 'status') {
			result = [...result].sort((a, b) => {
				const cmp = a.status.localeCompare(b.status)
				return sortDir === 'desc' ? -cmp : cmp
			})
		}

		return result
	}, [workflows, q, sortBy, sortDir])

	const handleArchive = useCallback(
		async (id: string) => {
			await archiveWorkflow(workflowsPort, id, notify)
			reload()
		},
		[reload],
	)

	const handleDelete = useCallback(
		async (id: string) => {
			await deleteWorkflow(workflowsPort, id, notify)
			reload()
		},
		[reload],
	)

	const meta = useMemo(
		() => ({
			archiveWorkflow: handleArchive,
			deleteWorkflow: handleDelete,
		}),
		[handleArchive, handleDelete],
	)

	const columns = useWorkflowColumns()
	const { table } = useDataTable<CompanyWorkflow>({
		data: filtered,
		columns,
		getRowId: row => row.id,
		meta,
	})

	return (
		<PageLayoutContainer>
			<WorkflowsListHeader table={table} />
			<WorkflowsTable data={filtered} hasFilters={Boolean(q)} table={table} />
			<WorkflowCreateSheet onSuccess={reload} />
		</PageLayoutContainer>
	)
}

export { WorkflowList }
